/**
 * Master data contracts (spec §13).
 *
 * Rejection reasons, clarification items and supporting-document categories are the same
 * shape — code, label, category, active, sort order — so they share one table keyed by
 * list type rather than three near-identical ones (§13.3 explicitly offers this).
 *
 * Deactivation, never deletion. A rejection from 2024 that referenced a reason must still
 * read correctly in 2027; hard-deleting the reason turns history into a dangling id.
 */
import { z } from 'zod';
import { masterDataListTypeSchema } from './enums.js';
export const masterDataItemSchema = z.object({
    id: z.string(),
    listType: masterDataListTypeSchema,
    /** Stable identifier used in reports, webhook payloads and integrator code. */
    code: z.string(),
    label: z.string(),
    category: z.string().nullable(),
    description: z.string().nullable(),
    active: z.boolean(),
    sortOrder: z.number().int(),
    /** null = a platform-global item inherited by every chamber. */
    chamberId: z.string().nullable(),
});
export const createMasterDataItemSchema = z.object({
    listType: masterDataListTypeSchema,
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(60)
        .regex(/^[A-Z0-9_]+$/, 'Use uppercase letters, numbers and underscores'),
    label: z.string().trim().min(1).max(200),
    category: z.string().trim().max(80).optional().nullable(),
    description: z.string().trim().max(500).optional().nullable(),
    sortOrder: z.number().int().min(0).max(9999).default(100),
    active: z.boolean().default(true),
});
export const updateMasterDataItemSchema = createMasterDataItemSchema
    .partial()
    .omit({ listType: true, code: true });
export const documentTypeSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    agreement: z.string().nullable(),
    description: z.string().nullable(),
    preferential: z.boolean(),
    active: z.boolean(),
    sortOrder: z.number().int(),
});
export const createDocumentTypeSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(60)
        .regex(/^[A-Z0-9_]+$/),
    name: z.string().trim().min(1).max(200),
    agreement: z.string().trim().max(120).optional().nullable(),
    description: z.string().trim().max(1000).optional().nullable(),
    /** A preferential certificate claims an FTA benefit; a generic COA does not. */
    preferential: z.boolean().default(true),
    sortOrder: z.number().int().default(100),
});
/**
 * The code is accepted here and then REFUSED by the service, rather than stripped.
 *
 * Omitting it from the schema is tidier and worse: zod drops unknown keys silently, so a
 * caller trying to rename a type got 200 and an unchanged record, and had no way to learn
 * that the field they sent was ignored. A code is what integrators submit against and what
 * every certificate issued under it refers to — refusing loudly is the point.
 */
export const updateDocumentTypeSchema = createDocumentTypeSchema
    .partial()
    .extend({ active: z.boolean().optional() });
/** What `GET /api/v1/document-types` returns, so an integrator can validate before
 *  submitting rather than discovering a rejection after preparing a document. */
export const chamberDocumentTypeSchema = z.object({
    code: z.string(),
    name: z.string(),
    agreement: z.string().nullable(),
    preferential: z.boolean(),
    /** False when the chamber has enabled the type but not yet configured stamping for it. */
    readyToIssue: z.boolean(),
    supportingDocumentCategories: z.array(z.object({ code: z.string(), label: z.string() })),
});
//# sourceMappingURL=master-data.js.map